import { Editor } from '@tinymce/tinymce-react';
import React, { FC, useRef } from 'react';
import type { Editor as TinyMCEEditor } from 'tinymce';
import contentCss from 'wfp-tinymce/js/tinymce/skins/content/default/content.min.css?inline';
import contentUiCss from 'wfp-tinymce/js/tinymce/skins/ui/oxide/content.min.css?inline';

import 'wfp-tinymce/js/tinymce/tinymce';
import 'wfp-tinymce/js/tinymce/models/dom/model';
import 'wfp-tinymce/js/tinymce/themes/silver/theme';
import 'wfp-tinymce/js/tinymce/icons/default/icons';
import 'wfp-tinymce/js/tinymce/skins/ui/oxide/skin.min.css';
import 'wfp-tinymce/js/tinymce/plugins/lists/plugin';
import 'wfp-tinymce/js/tinymce/plugins/link/plugin';

interface Props {
  content: string;
  onChange: (value: string) => void;
  editorRef?: React.MutableRefObject<TinyMCEEditor | null>;
  toolbar2?: string;
  allowLinks?: boolean;
  extendedValidElements?: string;
  extraContentStyle?: string;
}

const TinyEditorWrapper: FC<Props> = ({
  content,
  onChange,
  editorRef: externalEditorRef,
  toolbar2 = 'bold italic underline strikethrough superscript | removeformat | numlist bullist | indent outdent',
  allowLinks = false,
  extendedValidElements,
  extraContentStyle,
}) => {
  const internalEditorRef = useRef(null);
  const editorRef = externalEditorRef || internalEditorRef;

  function removeLinks(updatedContent: string) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = updatedContent;

    tempDiv.querySelectorAll('a').forEach(link => {
      if (!link.classList.contains('comment-anchor')) {
        const textNode = document.createTextNode(link.textContent);
        link.parentNode?.replaceChild(textNode, link);
      }
    });

    return tempDiv.innerHTML;
  }

  return (
    <Editor
      onInit={(_evt, editor) => {
        editorRef.current = editor;
        editor.on('BeforeSetContent', function (e) {
          e.content = e.content.replace(/<!--[\s\S]*?-->/g, '');
          if (!allowLinks) e.content = removeLinks(e.content);
        });

        editor.on('PostProcess', function (e) {
          if (e.set) {
            e.content = e.content.replace(/<!--[\s\S]*?-->/g, '');
            if (!allowLinks) e.content = removeLinks(e.content);
          }
        });

        // An AI proposal is not part of the narrative until it is accepted, and
        // accepting resolves the spans in the DOM first. So whatever the editor
        // serializes while a preview is open is the text as it was before it.
        editor.on('PreProcess', function (e) {
          const root = e.node;
          if (!root || typeof root.querySelectorAll !== 'function') return;

          root
            .querySelectorAll('[data-ai-preview="rephrase"]')
            .forEach((node: Element) => node.remove());

          root
            .querySelectorAll('[data-ai-preview="rephrase-old"]')
            .forEach((node: Element) => {
              while (node.firstChild) {
                node.parentNode?.insertBefore(node.firstChild, node);
              }
              node.remove();
            });
        });
      }}
      onEditorChange={onChange}
      value={content}
      init={{
        height: 500,
        menubar: false,
        skin: false,
        content_css: false,
        content_style: [contentCss, contentUiCss, extraContentStyle || '']
          .join('\n')
          .trim(),
        plugins: allowLinks ? 'lists link' : 'lists',
        branding: false,
        allow_html_in_named_anchor: true,
        toolbar1: allowLinks
          ? 'cut copy paste | undo redo | link'
          : 'cut copy paste | undo redo',
        toolbar2,
        browser_spellcheck: true,
        remove_trailing_brs: false,
        valid_elements: allowLinks
          ? `-a[id|name|class|style|href|target|rel],-strong/b,-em/i,-sup,-strike,-u,#p[style],-ul[type|compact|style],-ol[reversed|start|type|compact|style],#li[value|type|style],-s,br[clear],-span[style]`
          : `-a[id|name|class|style],-strong/b,-em/i,-sup,-strike,-u,#p[style],-ul[type|compact|style],-ol[reversed|start|type|compact|style],#li[value|type|style],-s,br[clear],-span[style]`,
        extended_valid_elements: extendedValidElements,
        valid_styles: {
          '*': 'padding-left',
        },
        allow_conditional_comments: false,
        link_title: false,
        default_link_target: '_blank',
        link_target_list: false,
        anchor_top: false,
        anchor_bottom: false,
        formats: {
          underline: { inline: 'u', remove: 'all' },
        },
        setup: editor => {
          if (allowLinks) {
            editor.on('NodeChange', () => {
              editor
                .getBody()
                .querySelectorAll('a[href]')
                .forEach(link => {
                  link.setAttribute('target', '_blank');
                  link.setAttribute('rel', 'noopener noreferrer');
                });
            });
          }
        },
      }}
    />
  );
};

export default TinyEditorWrapper;
